import { useState } from "react";
import TopBar from "../components/TopBar";
import Avatar from "../components/Avatar";
import Badge from "../components/Badge";
import Toast from "../components/Toast";
import { QUARTER_DEF, CLIENT_COLORS, uid } from "../constants";
import FieldRow from "../components/FieldRow";
import { apiFetch } from "../utils/api";
import { fmtDay, fmtDateTime, fmtDisplayDate, todayIST } from "../utils/dateUtils";

// Max reminder window after a cycle's deadline. Mirror of the same constant
// in controllers/cycleController.js — if you bump one, bump the other.
const MAX_REMINDER_DAYS_AFTER_DEADLINE = 90;
// Hard cap on how many reminders a cycle can have. Prevents the form from
// scrolling indefinitely while still giving plenty of touchpoints.
const MAX_REMINDERS = 3;
const addDaysIso = (iso, days) => {
  if (!iso) return "";
  // Parse parts manually instead of `new Date(iso)` — the latter treats
  // "YYYY-MM-DD" as UTC midnight which shifts to the previous day in
  // negative-UTC zones.
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
};

const NewCycleModal = ({ onAdd, onClose, existing }) => {
  // Default form: keep the first reminder slot present so R1 (which is
  // mandatory) is always there for the user to fill.
  const [f, setF] = useState({ year: new Date().getFullYear() + 1, quarter: "", start: "", endDate: "", reminders: [""] });
  const [touched, setTouched] = useState(false);
  const [apiError, setApiError] = useState("");
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const yr = parseInt(f.year);
  const usedQ = existing.filter(c => c.year === yr).map(c => c.quarter);

  // When year changes — reset everything (keep R1 slot present)
  const handleYearChange = (v) => setF({ year: v, quarter: "", start: "", endDate: "", reminders: [""] });

  // When quarter is selected — AUTO-POPULATE start, end_date, and reminder defaults.
  // Reminders fire AFTER the quarter ends because the review covers work
  // completed during the quarter, so the request mail goes out post-quarter:
  //   R1 = end_date + 30 days   R2 = end_date + 60 days
  // Date arithmetic also handles year-roll naturally (Q4 → next year reminders).
  const handleQuarterChange = (v) => {
    if (!v) { upd("quarter", ""); return; }
    const q = parseInt(v);
    const def = QUARTER_DEF[q];
    const endDateISO = `${yr}-${def.end}`;
    const r1ISO = addDaysIso(endDateISO, 30);
    const r2ISO = addDaysIso(endDateISO, 60);
    setF(p => ({ ...p, quarter: v, start: `${yr}-${def.start}`, endDate: endDateISO, reminders: [r1ISO, r2ISO] }));
  };

  // ── Dynamic reminder handlers ────────────────────────────────────────────
  const addReminder = () => setF(p => p.reminders.length >= MAX_REMINDERS ? p : ({ ...p, reminders: [...p.reminders, ""] }));
  const removeReminder = (i) => setF(p => ({ ...p, reminders: p.reminders.filter((_, j) => j !== i) }));
  const updReminder = (i, v) => setF(p => ({ ...p, reminders: p.reminders.map((r, j) => j === i ? v : r) }));

  // ── Validation ───────────────────────────────────────────────────────────
  const getErrors = () => {
    const errs = [];
    if (!f.quarter) { errs.push("Please select a quarter."); return errs; }
    if (!f.start) { errs.push("Start Date is required."); return errs; }
    if (!f.endDate) { errs.push("End Date is required."); return errs; }

    const startD = new Date(f.start);
    const endDateD = new Date(f.endDate);
    const q = parseInt(f.quarter);
    const def = QUARTER_DEF[q];

    if (startD.getFullYear() !== yr) errs.push(`Start Date must be in ${yr} — found ${startD.getFullYear()}.`);
    if (endDateD.getFullYear() !== yr) errs.push(`End Date must be in ${yr} — found ${endDateD.getFullYear()}.`);
    if (endDateD <= startD) errs.push("End Date must be after Start Date.");

    const qStartBound = new Date(`${yr}-${def.start}`);
    const qEndBound = new Date(`${yr}-${def.end}`);
    if (startD < qStartBound || startD > qEndBound) errs.push(`Start Date must fall within ${def.months} ${yr}.`);
    if (endDateD < qStartBound || endDateD > qEndBound) errs.push(`End Date must fall within ${def.months} ${yr}.`);

    // R1 is mandatory.
    if (!f.reminders[0]) errs.push("Reminder 1 is required.");

    // Reminders can fall anywhere from cycle start up to (end_date + 90 days).
    // The 90-day window lets admins schedule nudges past the official end date
    // for stragglers; reminders beyond that are rejected to prevent unbounded
    // scheduling.
    const reminderMaxISO = addDaysIso(f.endDate, MAX_REMINDER_DAYS_AFTER_DEADLINE);
    const reminderMaxD = reminderMaxISO ? new Date(reminderMaxISO) : null;

    let lastFilledD = startD;
    let lastFilledIdx = -1;
    f.reminders.forEach((r, i) => {
      if (!r) return;
      const rD = new Date(r);
      if (rD <= startD) errs.push(`Reminder ${i + 1} must be after Start Date.`);
      if (reminderMaxD && rD > reminderMaxD) errs.push(`Reminder ${i + 1} must be on or before ${reminderMaxISO} (End Date + ${MAX_REMINDER_DAYS_AFTER_DEADLINE} days).`);
      if (lastFilledIdx >= 0 && rD <= lastFilledD) errs.push(`Reminder ${i + 1} must be after Reminder ${lastFilledIdx + 1}.`);
      lastFilledD = rD;
      lastFilledIdx = i;
    });

    return errs;
  };

  const errors = getErrors();
  const isValid = errors.length === 0;

  const submit = async () => {
    setTouched(true);
    if (!isValid) return;
    const q = parseInt(f.quarter);
    const filled = f.reminders.filter(r => !!r);
    const newCycle = {
      id: "CY" + uid(),
      q: `Q${q} ${yr}`,
      year: yr, quarter: q,
      start: f.start, endDate: f.endDate,
      r1: filled[0] || "", r2: filled[1] || "", r3: filled[2] || "",
      reminders: filled,
      status: "Draft", sent: 0, submitted: 0, closed: false, emailHistory: []
    };
    try {
      setApiError("");
      const res = await apiFetch("/api/cycles", {
        method: "POST",
        body: JSON.stringify({
          id: newCycle.id,
          quarter_label: newCycle.q,
          year: newCycle.year,
          quarter_num: newCycle.quarter,
          start_date: newCycle.start,
          end_date: newCycle.endDate,
          // Send the full reminders array — backend persists each into
          // cycle_reminders (so the cron job fires all of them) and mirrors
          // the first three into r1_date/r2_date/r3_date for back-compat.
          reminders: filled,
        }),
      });
      const d = await res.json();
      if (!d.success) { setApiError(d.message || "Error creating cycle."); return; }
      const realId = d.data?.id || d.id || newCycle.id;
      onAdd({ ...newCycle, id: realId });
    } catch (e) { setApiError("Network error — cycle not saved."); }
  };

  const selDef = f.quarter ? QUARTER_DEF[parseInt(f.quarter)] : null;

  return (
    <div className="modal-overlay" style={{ left: 236 }}>
      <div className="modal" style={{ maxWidth: 560, width: "min(560px, calc(100% - 32px))" }}>
        {/* Header */}
        <div style={{
          padding: "22px 28px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "linear-gradient(135deg,#0D1B2A,#1E3A5F)", borderRadius: "14px 14px 0 0"
        }}>
          <div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#fff" }}>Create New Cycle</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.55)", marginTop: 2 }}>Select year & quarter — dates auto-fill instantly</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: 8, color: "#fff", padding: "5px 11px", fontSize: 18, cursor: "pointer" }}>&#215;</button>
        </div>

        <div style={{ padding: "22px 28px", display: "flex", flexDirection: "column", gap: 14, overflowY: "auto", maxHeight: "70vh" }}>

          {/* Year + Quarter */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FieldRow label="Year">
              <input className="inp" type="number" value={f.year}
                onChange={e => handleYearChange(e.target.value)} min={2025} max={2035}
                onKeyDown={(e) => { if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault(); }} />
            </FieldRow>
            <FieldRow label="Quarter *">
              <select className="inp" value={f.quarter} onChange={e => handleQuarterChange(e.target.value)}>
                <option value="">— Select quarter —</option>
                {[1, 2, 3, 4].map(q => {
                  const taken = usedQ.includes(q);
                  return (
                    <option key={q} value={q} disabled={taken}>
                      {QUARTER_DEF[q].label}{taken ? " · exists" : ""}
                    </option>
                  );
                })}
              </select>
            </FieldRow>
          </div>

          {/* Auto-fill confirmation pill */}
          {selDef && f.start && (
            <div style={{
              background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8,
              padding: "10px 14px", fontSize: 12, color: "#1E40AF", display: "flex", alignItems: "center", gap: 8
            }}>
              <span>📅</span>
              <span>Auto-set: <strong>{fmtDay(f.start)}</strong> to <strong>{fmtDay(f.endDate)}</strong>
                <span style={{ marginLeft: 8, opacity: .65 }}>({selDef.months} {yr})</span>
              </span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#94A3B8" }}>Locked to quarter</span>
            </div>
          )}

          {/* Start + End Date — both read-only, populated from quarter selection. */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FieldRow label="Start Date *">
              <input className="inp" type="date" value={fmtDay(f.start)} readOnly disabled
                title="Locked — auto-set by quarter selection"
                style={{ background: "#F8FAFC", color: "#64748B", cursor: "not-allowed" }} />
            </FieldRow>
            <FieldRow label="End Date *">
              <input className="inp" type="date" value={fmtDay(f.endDate)} readOnly disabled
                title="Locked — auto-set by quarter selection"
                style={{ background: "#F8FAFC", color: "#64748B", cursor: "not-allowed" }} />
            </FieldRow>
          </div>

          {/* Dynamic Reminders */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4 }}>
                Reminders <span style={{ color: "#EF4444" }}>*</span>
                <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#94A3B8", fontWeight: 400, textTransform: "none", lineHeight: 1.5 }}>
                  R1 is mandate. Up to {MAX_REMINDERS} reminders allowed. Reminders can be scheduled up to {MAX_REMINDER_DAYS_AFTER_DEADLINE} days from the end date.
                </span>
              </label>
              {(() => {
                const atMax = f.reminders.length >= MAX_REMINDERS;
                return (
                  <button onClick={addReminder} disabled={atMax}
                    title={atMax ? `Maximum ${MAX_REMINDERS} reminders allowed` : "Add another reminder date"}
                    style={{
                      background: atMax ? "#F8FAFC" : "#FFF5F0", border: `1.5px solid ${atMax ? "#E2E8F0" : "#FED7AA"}`, borderRadius: 8,
                      color: atMax ? "#94A3B8" : "#E8520A", fontSize: 11, fontWeight: 700, padding: "4px 10px",
                      cursor: atMax ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 4,
                    }}>
                    &#43; Add Reminder{atMax ? ` (max ${MAX_REMINDERS})` : ""}
                  </button>
                );
              })()}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {f.reminders.map((r, i) => {
                const isFirst = i === 0;
                const reminderMaxISO = addDaysIso(f.endDate, MAX_REMINDER_DAYS_AFTER_DEADLINE);
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: "50%", background: r ? "#E8520A" : (isFirst ? "#FEE2E2" : "#F1F5F9"),
                      color: r ? "#fff" : (isFirst ? "#EF4444" : "#94A3B8"), display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, fontWeight: 700, flexShrink: 0
                    }}>R{i + 1}</span>
                    <input className="inp" type="date" value={r} onChange={e => updReminder(i, e.target.value)}
                      min={f.start || undefined} max={reminderMaxISO || undefined}
                      required={isFirst}
                      style={{ flex: 1, borderColor: r ? "#FED7AA" : (isFirst ? "#FCA5A5" : "") }}
                      onClick={(e) => { e.target.showPicker?.(); }} />
                    {/* R1 is mandatory — only allow removing R2/R3/etc. */}
                    {!isFirst && (
                      <button onClick={() => removeReminder(i)} style={{
                        background: "none", border: "1.5px solid #FCA5A5", borderRadius: 6,
                        color: "#EF4444", width: 26, height: 26, cursor: "pointer", fontSize: 14, fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                      }}>&#215;</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Info banner */}
          <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#166534" }}>
            Cycle saves as <strong>Draft</strong>. Activate only after the quarter start date — early activation is blocked.
          </div>

          {/* Validation errors */}
          {touched && errors.length > 0 && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px" }}>
              {errors.map((e, i) => (
                <div key={i} style={{
                  fontSize: 12, color: "#EF4444", display: "flex", alignItems: "flex-start", gap: 6,
                  marginBottom: i < errors.length - 1 ? 4 : 0
                }}>
                  <span style={{ flexShrink: 0, marginTop: 1 }}>&#9679;</span> {e}
                </div>
              ))}
            </div>
          )}
          {touched && isValid && (
            <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "8px 14px", fontSize: 12, color: "#166534" }}>
              ✓ All fields valid — ready to create.
            </div>
          )}
        </div>

        {apiError && (
          <div style={{ margin: "0 28px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#B91C1C" }}>
            {apiError}
          </div>
        )}
        <div style={{ padding: "0 28px 24px", display: "flex", gap: 10 }}>
          <button className="btn-primary" style={{ flex: 1, justifyContent: "center", fontSize: 14 }} onClick={submit}>
            Create Cycle
          </button>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};
const Scheduler = ({ employees, cycles, setCycles, clients, topBarProps, cycleEmailState, setCycleEmailState, currentRole, allReviews, emailTemplates }) => {
  const allYears = [...new Set((cycles || []).map(c => c.year))].sort().reverse();
  const [selYear, setSelYear] = useState(new Date().getFullYear());
  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState(null);
  // Snapshot of the cycle before edits — restored on Cancel so unsaved
  // reminder removals / date edits don't persist. Cleared on successful Save.
  const [editSnapshot, setEditSnapshot] = useState(null);
  const beginEdit = (cyc) => {
    setEditSnapshot({
      id: cyc.id,
      start: cyc.start,
      endDate: cyc.endDate,
      r1: cyc.r1, r2: cyc.r2, r3: cyc.r3,
      reminders: [...(cyc.reminders || [])],
    });
    setEditId(cyc.id);
  };
  const cancelEdit = () => {
    if (editSnapshot) {
      setCycles(p => p.map(c => c.id === editSnapshot.id
        ? {
          ...c, start: editSnapshot.start, endDate: editSnapshot.endDate,
          r1: editSnapshot.r1, r2: editSnapshot.r2, r3: editSnapshot.r3,
          reminders: editSnapshot.reminders
        }
        : c));
    }
    setEditSnapshot(null);
    setEditId(null);
  };
  const [expanded, setExpanded] = useState({});
  const [histOpen, setHistOpen] = useState({});
  const [toast, setToast] = useState("");
  const [previewModal, setPreviewModal] = useState(null);

  // ── Template-driven email preview (same as EmailModule / Reviews) ────────────
  const TYPE_TO_TEMPLATE = {
    "Review Request": "request_review",
    "Reminder 1": "reminder_1",
    "Reminder 2": "reminder_2",
    "Reminder 3": "reminder_3",
  };
  const renderPreviewBody = () => {
    if (!previewModal) return "";
    const { cycId, shId, shName, empList, type, quarter, deadline } = previewModal;
    const tplType = TYPE_TO_TEMPLATE[type] || "request_review";
    const tpl = (emailTemplates || []).find(t => t.type === tplType);
    if (!tpl) return "(Template not found)";
    const origin = typeof window !== "undefined" ? window.location.origin : "https://depr.dolluzcorp.com";
    // Build employee list — each employee gets their individual review link
    const empStr = empList.map((e, i) => {
      const link = origin + "/review?r=RV_" + e.id + "_" + shId + "_" + cycId;
      return (i + 1) + ". " + e.name + (e.role ? " (" + e.role + ")" : "") + "\n   " + link;
    }).join("\n");
    return tpl.body
      .replace(/{{stakeholder_name}}/g, shName)
      .replace(/{{employee_list}}/g, empStr)
      .replace(/{{quarter}}/g, quarter)
      .replace(/{{deadline}}/g, expandMonth(fmtDisplayDate(deadline) || deadline || "[Deadline]"))
      .replace(/{{portal_link}}/g, origin + "/review")
      .replace(/{{admin_name}}/g, "Dolluz Corp Admin");
  };
  const expandMonth = (dateStr) => {
    if (!dateStr) return dateStr;
    const SHORT_TO_FULL = {
      Jan: "January", Feb: "February", Mar: "March", Apr: "April",
      May: "May", Jun: "June", Jul: "July", Aug: "August",
      Sep: "September", Oct: "October", Nov: "November", Dec: "December",
    };
    return String(dateStr).replace(
      /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/g,
      (m) => SHORT_TO_FULL[m] || m
    );
  };
  const renderPreviewSubject = () => {
    if (!previewModal) return "";
    const { type, quarter, deadline } = previewModal;
    const tplType = TYPE_TO_TEMPLATE[type] || "request_review";
    const tpl = (emailTemplates || []).find(t => t.type === tplType);
    if (!tpl) return "";
    return tpl.subject
      .replace(/{{quarter}}/g, quarter)
      .replace(/{{deadline}}/g, expandMonth(fmtDisplayDate(deadline) || deadline || "[Deadline]"));
  };
  const [shLogOpen, setShLogOpen] = useState({});
  const [toastType, setToastType] = useState("");
  const showToast = (msg, type = "") => { setToast(msg); setToastType(type); setTimeout(() => { setToast(""); setToastType(""); }, 2800); };

  const togExp = id => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const togHist = id => setHistOpen(p => ({ ...p, [id]: !p[id] }));
  const togShLog = key => setShLogOpen(p => ({ ...p, [key]: !p[key] }));
  const updCyc = (id, fld, v) => setCycles(p => p.map(c => c.id === id ? { ...c, [fld]: v } : c));
  // Reminder-array mutators used by the edit-cycle-dates row so admins can
  // tweak any of the N reminders without falling back to fixed r1/r2 fields.
  const updCycReminder = (id, idx, v) => setCycles(p => p.map(c => {
    if (c.id !== id) return c;
    const next = [...(c.reminders || [])];
    while (next.length <= idx) next.push("");
    next[idx] = v;
    return { ...c, reminders: next, r1: next[0] || "", r2: next[1] || "", r3: next[2] || "" };
  }));
  const addCycReminder = (id) => setCycles(p => p.map(c => {
    if (c.id !== id) return c;
    const cur = c.reminders || [];
    if (cur.length >= MAX_REMINDERS) return c;
    return { ...c, reminders: [...cur, ""] };
  }));
  const removeCycReminder = (id, idx) => setCycles(p => p.map(c => {
    if (c.id !== id) return c;
    const next = (c.reminders || []).filter((_, i) => i !== idx);
    return { ...c, reminders: next, r1: next[0] || "", r2: next[1] || "", r3: next[2] || "" };
  }));
  const closeCyc = async id => {
    const prev = [...cycles];
    setCycles(p => p.map(c => c.id === id ? { ...c, status: "Closed", closed: true } : c));
    try {
      const res = await apiFetch(`/api/cycles/${id}/close`, { method: "POST" });
      const d = await res.json();
      if (!d.success) { setCycles(prev); showToast("Error: " + (d.message || "Close failed"), "error"); return; }
      showToast("Cycle closed successfully");
    } catch (e) { setCycles(prev); showToast("Network error — cycle not closed", "error"); }
  };

  const reopenCyc = async id => {
    const cyc = cycles.find(c => c.id === id);
    const prev = [...cycles];
    setCycles(p => p.map(c => c.id === id ? { ...c, status: "Scheduled", closed: false } : c));
    try {
      const res = await apiFetch(`/api/cycles/${id}/reopen`, { method: "POST" });
      const d = await res.json();
      if (!d.success) { setCycles(prev); showToast("Error: " + (d.message || "Reopen failed"), "error"); return; }
      showToast((cyc?.q || "Cycle") + " re-opened — status reset to Scheduled");
    } catch (e) { setCycles(prev); showToast("Network error — cycle not re-opened", "error"); }
  };

  const typeToField = {
    "Review Request": "requestAt",
    "Reminder 1": "reminder1At",
    "Reminder 2": "reminder2At",
    "Reminder 3": "reminder3At",
  };

  const sendStakeholderEmail = async (cycId, clId, shId, shName, shEmail, empList, quarter, type) => {
    const key = cycId + "_" + clId + "_" + shId;
    const field = typeToField[type] || "requestAt";
    const now = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    const newLogs = empList.map(emp => ({ empId: emp.id, empName: emp.name, type, at: now }));
    setPreviewModal(null);
    try {
      const typeMap = { "Review Request": "request", "Reminder 1": "reminder1", "Reminder 2": "reminder2", "Reminder 3": "reminder3" };
      const res = await apiFetch(`/api/email-dispatch/send`, { method: "POST", body: JSON.stringify({ cycle_id: cycId, client_id: clId, stakeholder_id: shId, email_type: typeMap[type] || type, employee_ids: empList.map(e => e.id) }) });
      const d = await res.json();
      if (!d.success) { showToast("Email error: " + (d.message || "Failed to send"), "error"); return; }
      // Only update UI state after confirmed API success
      setCycleEmailState(p => {
        const prev = p[key] || { requestAt: null, reminder1At: null, reminder2At: null, reminder3At: null, logs: [] };
        return { ...p, [key]: { ...prev, [field]: now, logs: [...(prev.logs || []), ...newLogs] } };
      });
      setCycles(p => p.map(c => c.id === cycId ? {
        ...c,
        sent: type === "Review Request" ? c.sent + empList.length : c.sent,
        emailHistory: [{
          at: now, type, recipients: empList.length,
          notes: type + " → " + shName + " (" + shEmail + ") for " + empList.length + " employee" + (empList.length === 1 ? "" : "s")
        }, ...c.emailHistory]
      } : c));
      showToast(type + " sent to " + shName + " for " + empList.length + " employee" + (empList.length === 1 ? "" : "s"));
    } catch (e) { showToast("Network error — email not sent", "error"); }
  };

  const sendClientBulkEmail = async (cycId, clId, clName, shs, empsByShId, quarter, type) => {
    const now = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    const field = typeToField[type] || "requestAt";
    const totalEmps = Object.values(empsByShId).reduce((s, a) => s + a.length, 0);
    try {
      const typeMap = { "Review Request": "request", "Reminder 1": "reminder1", "Reminder 2": "reminder2", "Reminder 3": "reminder3" };
      const allEmpIds = Object.values(empsByShId).flat().map(e => e.id);
      const res = await apiFetch(`/api/email-dispatch/send-bulk`, { method: "POST", body: JSON.stringify({ cycle_id: cycId, client_id: clId, email_type: typeMap[type] || type, employee_ids: allEmpIds }) });
      const d = await res.json();
      if (!d.success) { showToast("Bulk email error: " + (d.message || "Failed to send"), "error"); return; }
      // Only update UI state after confirmed API success
      setCycleEmailState(p => {
        const next = { ...p };
        shs.forEach(sh => {
          const key = cycId + "_" + clId + "_" + sh.id;
          const prev = next[key] || { requestAt: null, reminder1At: null, reminder2At: null, reminder3At: null, logs: [] };
          const emps = empsByShId[sh.id] || [];
          const newLogs = emps.map(emp => ({ empId: emp.id, empName: emp.name, type, at: now }));
          next[key] = { ...prev, [field]: now, logs: [...(prev.logs || []), ...newLogs] };
        });
        return next;
      });
      setCycles(p => p.map(c => c.id === cycId ? {
        ...c,
        sent: type === "Review Request" ? c.sent + totalEmps : c.sent,
        emailHistory: [{
          at: now, type, recipients: shs.length,
          notes: "Bulk " + type + " → " + shs.length + " stakeholder" + (shs.length === 1 ? "" : "s") + " of " + clName
        }, ...c.emailHistory]
      } : c));
      showToast("Bulk " + type + " sent to " + shs.length + " stakeholder" + (shs.length === 1 ? "" : "s") + " of " + clName);
    } catch (e) { showToast("Network error — bulk email not sent", "error"); }
  };

  const openPreview = (cycId, clId, shId, shName, shEmail, empList, type, quarter, deadline) => {
    setPreviewModal({ cycId, clId, shId, shName, shEmail, empList, type, quarter, deadline });
  };

  const activateCyc = async id => {
    const cyc = cycles.find(c => c.id === id);
    if (!cyc) return;

    const sorted = [...cycles].sort((a, b) => (a.year * 10 + a.quarter) - (b.year * 10 + b.quarter));
    const activeCycles = cycles.filter(c => c.status === "Active");
    const activeCount = activeCycles.length;
    const targetIdx = sorted.findIndex(c => c.id === id);
    const prevCyc = targetIdx > 0 ? sorted[targetIdx - 1] : null;

    // Guard 1: max 2 active at a time
    if (activeCount >= 2) {
      showToast("2 cycles are already active — close one before activating another");
      return;
    }

    // Guard 2: predecessor must be Active or Closed (no skipping Scheduled cycles)
    if (prevCyc && prevCyc.status !== "Active" && prevCyc.status !== "Closed") {
      showToast(cyc.q + " cannot be activated — " + prevCyc.q + " must be Active or Closed first");
      return;
    }

    // Guard 3: start date guard only when 0 cycles active (pre-activating with an active cycle skips this)
    if (activeCount === 0 && cyc.start) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const startD = new Date(cyc.start); startD.setHours(0, 0, 0, 0);
      if (today < startD) {
        const diff = Math.ceil((startD - today) / (1000 * 60 * 60 * 24));
        showToast("Cannot activate " + cyc.q + " yet — starts in " + diff + " day" + (diff === 1 ? "" : "s") + " (" + cyc.start + ")");
        return;
      }
    }

    const prev = [...cycles];
    setCycles(p => p.map(c => c.id === id ? { ...c, status: "Active" } : c));
    try {
      const res = await apiFetch(`/api/cycles/${id}/activate`, { method: "POST" });
      const d = await res.json();
      if (!d.success) { setCycles(prev); showToast("Error: " + (d.message || "Activate failed"), "error"); return; }
      const msg = activeCount === 1
        ? cyc.q + " activated alongside " + activeCycles[0].q + " — 2 cycles now running"
        : cyc.q + " activated successfully";
      showToast(msg);
    } catch (e) { setCycles(prev); showToast("Network error — cycle not activated", "error"); }
  };

  // Returns null if cycle can be activated, or a short reason string if blocked
  // Used to disable the Activate button and show tooltip text
  const getActivateBlock = (c) => {
    const sorted = [...cycles].sort((a, b) => (a.year * 10 + a.quarter) - (b.year * 10 + b.quarter));
    const activeCycles = cycles.filter(x => x.status === "Active");
    const activeCount = activeCycles.length;

    if (activeCount >= 2)
      return "2 cycles active — close one first";

    const targetIdx = sorted.findIndex(x => x.id === c.id);
    const prevCyc = targetIdx > 0 ? sorted[targetIdx - 1] : null;
    if (prevCyc && prevCyc.status !== "Active" && prevCyc.status !== "Closed")
      return prevCyc.q + " must be Active or Closed first";

    // Start date guard only when 0 cycles active
    if (activeCount === 0 && c.start) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const startD = new Date(c.start); startD.setHours(0, 0, 0, 0);
      if (today < startD) {
        const diff = Math.ceil((startD - today) / (1000 * 60 * 60 * 24));
        return "Starts in " + diff + " day" + (diff === 1 ? "" : "s");
      }
    }
    return null;
  };

  const addCycle = nc => {
    setCycles(p => [...p, nc]);
    setSelYear(nc.year);
    setShowNew(false);
    showToast("Cycle created — now viewing " + nc.year);
  };

  const yearCycles = cycles.filter(c => c.year === selYear).sort((a, b) => a.quarter - b.quarter);

  // ── Derive year label from actual cycle data — no hardcoded years ──────────
  // "CURRENT" = the year that contains at least one Active cycle
  // "CLOSED"  = every cycle in that year is Closed
  // "UPCOMING"= year has cycles but none Active and none Closed yet
  // (no label) = newly created year with only Draft cycles
  const getYearLabel = (y) => {
    const yCycles = cycles.filter(c => c.year === y);
    if (yCycles.length === 0) return null;
    const hasActive = yCycles.some(c => c.status === "Active");
    const allClosed = yCycles.every(c => c.closed);
    if (hasActive) return { text: "CURRENT", bg: "#10B981" };
    if (allClosed) return { text: "CLOSED", bg: "#94A3B8" };
    const hasAnyScheduled = yCycles.some(c => c.status === "Scheduled");
    if (hasAnyScheduled) return { text: "UPCOMING", bg: "#6366F1" };
    return null; // Draft-only years get no badge
  };

  // Summary stats for selected year (for closed years)
  const closedYear = yearCycles.every(c => c.closed);
  const yearStats = {
    totalSent: yearCycles.reduce((s, c) => s + c.sent, 0),
    totalSubmitted: yearCycles.reduce((s, c) => s + c.submitted, 0),
    totalEmails: yearCycles.reduce((s, c) => s + c.emailHistory.length, 0),
  };

  return (
    <div className="fade-in">
      <TopBar title="Cycle Scheduler" subtitle="Configure and manage quarterly review cycles with auto-trigger" {...topBarProps} />

      {/* ── Year selector + action ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#64748B" }}>Year:</span>
        {allYears.map(y => {
          const lbl = getYearLabel(y);
          return (
            <button key={y} className={`tab${selYear === y ? " active" : ""}`} onClick={() => { setSelYear(y); cancelEdit(); }}>
              {y}
              {lbl && (
                <span style={{
                  marginLeft: 5, fontSize: 9, background: selYear === y ? "rgba(255,255,255,.3)" : lbl.bg,
                  color: "#fff", padding: "1px 6px", borderRadius: 100, fontWeight: 700
                }}>
                  {lbl.text}
                </span>
              )}
            </button>
          );
        })}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {!closedYear && currentRole !== "Sub-Admin" && <button className="btn-primary" onClick={() => setShowNew(true)}>+ Create New Cycle</button>}
          {closedYear && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => showToast("Go to Reports page for full year exports", "")}>📊 View in Reports</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Closed year summary bar ── */}
      {closedYear && (
        <div style={{
          background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 12, padding: "14px 20px", marginBottom: 18,
          display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap"
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>📁 {selYear} — Full Year Summary</div>
          <div style={{ display: "flex", gap: 20 }}>
            {[
              { l: "Cycles Run", v: yearCycles.length, c: "#0D1B2A" },
              { l: "Total Sent", v: yearStats.totalSent, c: "#3B82F6" },
              { l: "Submitted", v: yearStats.totalSubmitted, c: "#10B981" },
              { l: "Emails Sent", v: yearStats.totalEmails, c: "#8B5CF6" },
            ].map(s => (
              <div key={s.l} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 20, color: s.c }}>{s.v}</div>
                <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Cycle cards ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {yearCycles.map(c => {
          const isEd = editId === c.id && !c.closed;
          const isExp = expanded[c.id];
          const isHist = histOpen[c.id];
          const headerBg = c.status === "Active" ? "#E8520A" : c.closed ? "#64748B" : "#F1F5F9";
          const headerFg = (c.status === "Active" || c.closed) ? "#fff" : "#94A3B8";
          // Completion measures submissions against ALL reviews in the cycle
          // (including not-yet-requested rows), matching the right-side
          // "submitted/total" display. Prevents the misleading 100% completion
          // when only Initiated reviews exist and all of them happen to be
          // submitted but Not Started rows are still pending.
          const completionPct = c.totalReviews > 0 ? Math.round((c.submitted / c.totalReviews) * 100) : 0;

          return (
            <div key={c.id} className="card" style={{
              border: c.status === "Active" ? "2px solid rgba(232,82,10,.22)" : c.closed ? "1.5px solid #E2E8F0" : "1.5px solid #E2E8F0",
              overflow: "hidden", opacity: c.closed ? 0.92 : 1
            }}>

              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {/* Quarter badge */}
                  <div style={{
                    width: 56, height: 56, borderRadius: 12, flexShrink: 0, background: headerBg, color: headerFg,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    fontFamily: "'Sora',sans-serif", fontWeight: 800
                  }}>
                    <div style={{ fontSize: 13 }}>{(c.q || "").split(" ")[0]}</div>
                    <div style={{ fontSize: 10, opacity: 0.75 }}>{c.year}</div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0D1B2A" }}>{c.q}</span>
                      <Badge status={c.status} />
                      {c.status === "Active" && (
                        <span style={{ fontSize: 11, color: "#E8520A", background: "#FFF5F0", padding: "2px 8px", borderRadius: 100, fontWeight: 600 }}>
                          {c.notStarted} Not Started · {c.pending} Pending · {c.submitted} Submitted
                        </span>
                      )}
                      {c.closed && c.totalReviews > 0 && (
                        <span style={{ fontSize: 11, color: "#10B981", background: "#F0FDF4", padding: "2px 8px", borderRadius: 100, fontWeight: 600 }}>
                          {completionPct}% Completion · {c.submitted}/{c.totalReviews} Submitted
                        </span>
                      )}
                    </div>

                    {/* Dates row */}
                    {isEd ? (
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                        {(() => {
                          // Reminders can extend up to end_date + MAX_REMINDER_DAYS_AFTER_DEADLINE.
                          const reminderMax = addDaysIso(c.endDate, MAX_REMINDER_DAYS_AFTER_DEADLINE);
                          // Start + End Date are locked (auto-set from the quarter on creation);
                          // admins can only adjust reminders here.
                          const fixed = [["start", "Start"], ["endDate", "End Date"]].map(([fld, lbl]) => (
                            <div key={fld}>
                              <label style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600, display: "block", marginBottom: 3, textTransform: "uppercase" }}>{lbl}</label>
                              <input type="date" value={c[fld] || ""} readOnly disabled
                                title="Locked — set by quarter selection"
                                style={{ fontSize: 12, padding: "5px 9px", width: "auto", background: "#F8FAFC", color: "#64748B", cursor: "not-allowed" }} />
                            </div>
                          ));

                          // Compute which reminder numbers (1-3) have already
                          // been sent for THIS cycle (manual click OR cron
                          // auto-send — both write to cycle_stakeholder_email_state).
                          // If a reminder has gone out, we lock its date input
                          // and disable the × delete so it can't be retroactively
                          // edited away.
                          const sentReminderNums = new Set();
                          for (const [k, st] of Object.entries(cycleEmailState || {})) {
                            if (!k.startsWith(c.id + "_") || !st) continue;
                            if (st.reminder1At) sentReminderNums.add(1);
                            if (st.reminder2At) sentReminderNums.add(2);
                            if (st.reminder3At) sentReminderNums.add(3);
                          }

                          // "Too soon to delete" — reminder fires within the
                          // next 24h (today or tomorrow), don't let admin yank it.
                          const todayISO = todayIST();
                          const tomorrowISO = addDaysIso(todayISO, 1);
                          const isTooSoon = (iso) => iso && iso <= tomorrowISO;

                          const reminders = (c.reminders && c.reminders.length) ? c.reminders : [""];
                          const reminderInputs = reminders.map((rDate, i) => {
                            const isR1 = i === 0;
                            const num = i + 1;
                            const sent = sentReminderNums.has(num);
                            const tooSoon = isTooSoon(rDate);
                            const lbl = `Reminder ${num}${isR1 ? " *" : ""}${sent ? " · Sent" : ""}`;
                            const lockReason = sent
                              ? `Reminder ${num} has already been sent — can't edit or delete.`
                              : (tooSoon ? `Reminder ${num} fires within 24 hours — can't delete this close to send time.` : "");
                            return (
                              <div key={`r${i}`} style={{ display: "flex", alignItems: "flex-end", gap: 4 }}>
                                <div>
                                  <label style={{ fontSize: 10, color: sent ? "#10B981" : (isR1 && !rDate ? "#EF4444" : "#94A3B8"), fontWeight: 600, display: "block", marginBottom: 3, textTransform: "uppercase" }}>{lbl}</label>
                                  <input type="date" value={rDate || ""} onChange={e => updCycReminder(c.id, i, e.target.value)}
                                    min={c.start || undefined}
                                    max={reminderMax || undefined}
                                    required={isR1}
                                    readOnly={sent}
                                    disabled={sent}
                                    title={sent ? lockReason : undefined}
                                    style={{
                                      fontSize: 12, padding: "5px 9px", width: "auto",
                                      borderColor: isR1 && !rDate ? "#FCA5A5" : undefined,
                                      background: sent ? "#F0FDF4" : undefined,
                                      color: sent ? "#065F46" : undefined,
                                      cursor: sent ? "not-allowed" : undefined,
                                    }}
                                    onClick={(e) => { if (!sent) e.target.showPicker?.(); }} />
                                </div>
                                {/* Delete: R1 is mandatory (never deletable);
                                    R2/R3 are deletable only if not sent AND not within 24h of firing. */}
                                {!isR1 && (
                                  <button onClick={() => { if (!sent && !tooSoon) removeCycReminder(c.id, i); }}
                                    disabled={sent || tooSoon}
                                    title={lockReason || `Remove Reminder ${num}`}
                                    style={{
                                      background: "none",
                                      border: `1.5px solid ${(sent || tooSoon) ? "#E2E8F0" : "#FCA5A5"}`,
                                      borderRadius: 6,
                                      color: (sent || tooSoon) ? "#CBD5E1" : "#EF4444",
                                      width: 22, height: 22,
                                      cursor: (sent || tooSoon) ? "not-allowed" : "pointer",
                                      fontSize: 12, fontWeight: 700, padding: 0,
                                    }}>
                                    &#215;
                                  </button>
                                )}
                              </div>
                            );
                          });
                          // "+ Reminder" button when below the cap.
                          const canAdd = reminders.length < MAX_REMINDERS;
                          const addBtn = (
                            <button key="add" onClick={() => addCycReminder(c.id)} disabled={!canAdd}
                              title={canAdd ? "Add another reminder" : `Maximum ${MAX_REMINDERS} reminders allowed`}
                              style={{
                                fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 6,
                                background: canAdd ? "#FFF5F0" : "#F8FAFC",
                                border: `1.5px solid ${canAdd ? "#FED7AA" : "#E2E8F0"}`,
                                color: canAdd ? "#E8520A" : "#94A3B8",
                                cursor: canAdd ? "pointer" : "not-allowed",
                              }}>
                              + Reminder
                            </button>
                          );
                          return [...fixed, ...reminderInputs, addBtn];
                        })()}
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 20, fontSize: 12, color: "#64748B", flexWrap: "wrap" }}>
                        <span>Start: <strong style={{ color: "#0D1B2A" }}>{fmtDay(c.start)}</strong></span>
                        <span>End: <strong style={{ color: "#0D1B2A" }}>{fmtDay(c.endDate)}</strong></span>
                        {(c.reminders || []).map((rDate, i) => rDate && (
                          <span key={`d${i}`}>R{i + 1}: <strong style={{ color: "#0D1B2A" }}>{fmtDay(rDate)}</strong></span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Progress counter for active/closed */}
                  {(c.status === "Active" || (c.closed && c.totalReviews > 0)) && (
                    <div style={{ textAlign: "right", flexShrink: 0, marginRight: 8 }}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0D1B2A" }}>{c.submitted}/{c.totalReviews}</div>
                      <div style={{ fontSize: 11, color: "#64748B" }}>Submitted</div>
                      <div className="progress-bar" style={{ marginTop: 6, width: 100 }}>
                        <div className="progress-fill" style={{ width: `${completionPct}%`, background: c.closed ? "#10B981" : "#E8520A" }} />
                      </div>
                    </div>
                  )}

                  {/* Action buttons — suppressed for closed cycles */}
                  {!c.closed && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                      {isEd ? (
                        <>
                          <button className="btn-primary" style={{ fontSize: 12 }} onClick={async () => {
                            const prev = cycles.find(x => x.id === c.id);
                            try {
                              const res = await apiFetch(`/api/cycles/${c.id}`, {
                                method: "PUT",
                                body: JSON.stringify({
                                  // start_date and end_date are read-only in the UI — but
                                  // we still send them so the validator can re-check the
                                  // reminder window against them.
                                  start_date: c.start,
                                  end_date: c.endDate,
                                  // Send the full reminders[] array — backend persists each
                                  // into cycle_reminders + mirrors first 3 into r1/r2/r3.
                                  reminders: (c.reminders || []).filter(Boolean),
                                }),
                              });
                              const d = await res.json();
                              if (!d.success) {
                                // Revert local edits on a failed save so the UI matches the server.
                                cancelEdit();
                                showToast(d.message || "Save failed", "error");
                                return;
                              }
                              setEditSnapshot(null);
                              setEditId(null);
                              showToast("Cycle dates saved");
                            } catch (e) {
                              cancelEdit();
                              showToast("Network error — dates not saved", "error");
                            }
                          }}>Save</button>
                          <button className="btn-ghost" style={{ fontSize: 12 }} onClick={cancelEdit}>Cancel</button>
                        </>
                      ) : (
                        <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => beginEdit(c)}>Edit Dates</button>
                      )}
                      {currentRole !== "Sub-Admin" && (c.status === "Scheduled" || c.status === "Draft") && !isEd && (() => {
                        const block = getActivateBlock(c);
                        return block ? (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <button className="btn-ghost" style={{ fontSize: 12, opacity: 0.45, cursor: "not-allowed", border: "1.5px dashed #CBD5E1", color: "#94A3B8" }} disabled>Activate</button>
                            <span style={{ fontSize: 9, color: "#EF4444", fontWeight: 600, textAlign: "center", maxWidth: 90, lineHeight: 1.3 }}>{block}</span>
                          </div>
                        ) : (
                          <button
                            style={{
                              fontSize: 12, padding: "7px 14px", background: "#fff", border: "1.5px solid #CBD5E1",
                              borderRadius: 8, cursor: "pointer", color: "#475569", fontWeight: 600, transition: "all .15s"
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "#E8520A"; e.currentTarget.style.color = "#E8520A"; e.currentTarget.style.background = "#FFF5F0"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.color = "#475569"; e.currentTarget.style.background = "#fff"; }}
                            onClick={() => activateCyc(c.id)}>
                            Activate
                          </button>
                        );
                      })()}
                      {currentRole !== "Sub-Admin" && c.status === "Active" && !isEd && <button className="btn-danger" style={{ fontSize: 12 }} onClick={() => closeCyc(c.id)}>Close Cycle</button>}
                    </div>
                  )}

                  {c.closed && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                      <button className="btn-ghost" style={{ fontSize: 11 }}
                        onClick={() => topBarProps?.setPage?.("reports")}>
                        📄 View Report
                      </button>
                      {currentRole !== "Sub-Admin" && (
                        <button className="btn-secondary" style={{ fontSize: 11 }}
                          onClick={() => reopenCyc(c.id)}>
                          ↩ Re-open
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Expand tabs */}
              <div style={{ borderTop: "1px solid #F1F5F9", display: "flex" }}>
                <button onClick={() => togExp(c.id)} style={{
                  flex: 1, padding: "10px 20px", background: "#FAFBFF", border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, color: "#475569", display: "flex", alignItems: "center", gap: 6, borderRight: "1px solid #F1F5F9"
                }}>
                  {(() => {
                    const n = clients.filter(cl => employees.some(e => e.allocations.some(a => a.clientId === cl.id))).length;
                    return <>Client &amp; Stakeholder Breakdown ({n} client{n !== 1 ? "s" : ""}) {isExp ? "▲" : "▼"}</>;
                  })()}
                </button>
                <button onClick={() => togHist(c.id)} style={{
                  flex: 1, padding: "10px 20px", background: "#FAFBFF", border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, color: "#475569", display: "flex", alignItems: "center", gap: 6
                }}>
                  Email Trigger History ({c.emailHistory.length}) {isHist ? "▲" : "▼"}
                </button>
              </div>

              {isExp && (
                <div className="slide-down" style={{ borderTop: "1px solid #F1F5F9", background: "#FAFBFF" }}>
                  {clients.filter(cl => employees.some(e => e.allocations.some(a => a.clientId === cl.id))).map((cl, clIdx) => {
                    const clEmps = employees.filter(e => e.allocations.some(a => a.clientId === cl.id))
                      .sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true, sensitivity: "base" }));
                    const clColor = cl.color || cl.color_hex || CLIENT_COLORS[cl.id] || "#64748B";
                    const activeStakeholders = cl.stakeholders.filter(s => s.active);

                    // Build empsByShId for bulk send
                    const empsByShId = {};
                    activeStakeholders.forEach(sh => {
                      empsByShId[sh.id] = clEmps.filter(emp => {
                        const alloc = emp.allocations.find(a => a.clientId === cl.id);
                        if (!alloc) return false;
                        if (alloc.stakeholders && alloc.stakeholders.length > 0) {
                          return alloc.stakeholders.some(s => s.stakeholderId === sh.id);
                        }
                        if (sh.level === "dept") return alloc.deptId === sh.deptId;
                        const pid = cl.primaryStakeholderId
                          ? cl.primaryStakeholderId
                          : (cl.stakeholders.filter(s => s.active && s.level === "client")[0] || {}).id;
                        return pid && pid === sh.id;
                      });
                    });

                    // allSent = every (stakeholder, employee) pair has a request log.
                    // Stakeholder-aggregate st.requestAt would incorrectly hide the
                    // Request All button as soon as ANY one employee gets a request,
                    // leaving newly-added employees stranded. Check the per-employee
                    // logs against each stakeholder's employee set.
                    const _normPair = t => String(t || "").trim().toLowerCase().replace(/\s+/g, "");
                    const _isReqPair = l => { const s = _normPair(l && l.type); return s === "request" || s === "reviewrequest"; };
                    const allSent = activeStakeholders.length > 0 && activeStakeholders.every(sh => {
                      const key = c.id + "_" + cl.id + "_" + sh.id;
                      const st = cycleEmailState[key] || {};
                      const requested = new Set((st.logs || []).filter(_isReqPair).map(l => l.empId));
                      const emps = empsByShId[sh.id] || [];
                      return emps.length === 0 || emps.every(e => requested.has(e.code) || requested.has(e.id));
                    });
                    // Bulk-send target: for each stakeholder, the employees that
                    // haven't been requested yet. Empty entries are dropped.
                    const empsByShIdPending = {};
                    activeStakeholders.forEach(sh => {
                      const key = c.id + "_" + cl.id + "_" + sh.id;
                      const st = cycleEmailState[key] || {};
                      const requested = new Set((st.logs || []).filter(_isReqPair).map(l => l.empId));
                      const pending = (empsByShId[sh.id] || []).filter(e => !requested.has(e.code) && !requested.has(e.id));
                      if (pending.length > 0) empsByShIdPending[sh.id] = pending;
                    });
                    const bulkStakeholders = activeStakeholders.filter(sh => empsByShIdPending[sh.id]);
                    const bulkPendingTotal = Object.values(empsByShIdPending).reduce((s, a) => s + a.length, 0);

                    return (
                      <div key={cl.id} style={{ borderBottom: clIdx < clients.filter(cl2 => employees.some(e => e.allocations.some(a => a.clientId === cl2.id))).length - 1 ? "1.5px solid #E2E8F0" : "none" }}>

                        {/* ── Client header row ── */}
                        <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, background: "#fff" }}>
                          <div style={{ width: 12, height: 12, borderRadius: "50%", background: clColor, flexShrink: 0 }} />
                          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", flex: 1 }}>{cl.name}</div>
                          <span style={{ fontSize: 11, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 100, padding: "2px 9px", color: "#64748B", fontWeight: 600 }}>
                            {clEmps.length} employee{clEmps.length !== 1 ? "s" : ""}
                          </span>
                          <span style={{ fontSize: 11, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 100, padding: "2px 9px", color: "#64748B", fontWeight: 600 }}>
                            {activeStakeholders.length} stakeholder{activeStakeholders.length !== 1 ? "s" : ""}
                          </span>
                          {!c.closed && currentRole !== "Sub-Admin" && (
                            allSent ? (
                              <span style={{ fontSize: 11, color: "#10B981", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>&#10003; All Requested</span>
                            ) : c.status !== "Active" ? (
                              <span title="Activate the cycle first to send emails"
                                style={{ fontSize: 11, color: "#94A3B8", background: "#F8FAFC", border: "1.5px dashed #CBD5E1", borderRadius: 100, padding: "4px 12px", fontWeight: 600, cursor: "not-allowed" }}>
                                🔒 Activate First
                              </span>
                            ) : (
                              <button className="btn-secondary" style={{ fontSize: 11, padding: "5px 12px", display: "flex", alignItems: "center", gap: 5 }}
                                title={`Send Review Request to ${bulkPendingTotal} pending employee${bulkPendingTotal !== 1 ? "s" : ""}`}
                                onClick={() => sendClientBulkEmail(c.id, cl.id, cl.name,
                                  bulkStakeholders,
                                  empsByShIdPending, c.q, "Review Request")}>
                                &#128231; Request All{bulkPendingTotal > 0 && bulkPendingTotal < clEmps.length ? ` (${bulkPendingTotal})` : ""}
                              </button>
                            )
                          )}
                        </div>

                        {/* ── Stakeholder rows ── */}
                        {activeStakeholders.length === 0 && (
                          <div style={{ padding: "12px 20px 12px 44px", fontSize: 12, color: "#94A3B8", fontStyle: "italic", background: "#FAFBFF" }}>
                            No active stakeholders configured for this client.
                          </div>
                        )}
                        {activeStakeholders.map((sh, shIdx) => {
                          const shEmps = clEmps.filter(emp => {
                            const alloc = emp.allocations.find(a => a.clientId === cl.id);
                            if (!alloc) return false;

                            // ── Custom stakeholder split: employee appears ONLY under
                            //    the specific stakeholders listed in the split ──────────
                            if (alloc.stakeholders && alloc.stakeholders.length > 0) {
                              return alloc.stakeholders.some(s => s.stakeholderId === sh.id);
                            }

                            // ── Default (no custom split) ─────────────────────────────
                            // Dept-level stakeholder → filter by deptId match
                            if (sh.level === "dept") {
                              return alloc.deptId === sh.deptId;
                            }

                            // Client-level stakeholder → only show under the PRIMARY.
                            // Use explicit primaryStakeholderId if set, else first active client-level.
                            const primaryId = cl.primaryStakeholderId
                              ? cl.primaryStakeholderId
                              : (cl.stakeholders.filter(s => s.active && s.level === "client")[0] || {}).id;
                            return primaryId && primaryId === sh.id;
                          });
                          if (shEmps.length === 0) return null;
                          const shKey = c.id + "_" + cl.id + "_" + sh.id;
                          const shState = cycleEmailState[shKey] || { requestAt: null, reminder1At: null, reminder2At: null, reminder3At: null, logs: [] };

                          // ── Per-employee email state (replaces stakeholder-aggregate) ──
                          // The cycle_stakeholder_email_state row's request_at /
                          // reminder*_at columns track "did SOMEONE get this" —
                          // they leak Pavithran's sends into Sobana's status when
                          // both share a stakeholder. Derive each step's progress
                          // from the per-employee email_logs entries instead.
                          // normLogType unifies backend ("request"/"reminder1") and
                          // optimistic-UI ("Review Request"/"Reminder 1") strings.
                          const _norm = t => String(t || "").trim().toLowerCase().replace(/\s+/g, "");
                          const _isReq = l => { const s = _norm(l && l.type); return s === "request" || s === "reviewrequest"; };
                          const _isRem = (l, n) => _norm(l && l.type) === "reminder" + n;
                          const _empLogs = shState.logs || [];
                          const reqSentSet = new Set(_empLogs.filter(_isReq).map(l => l.empId));
                          const r1SentSet = new Set(_empLogs.filter(l => _isRem(l, 1)).map(l => l.empId));
                          const r2SentSet = new Set(_empLogs.filter(l => _isRem(l, 2)).map(l => l.empId));
                          const r3SentSet = new Set(_empLogs.filter(l => _isRem(l, 3)).map(l => l.empId));
                          // Match on either emp.code or emp.id (optimistic UI on Scheduler logs
                          // `emp.id`; backend logs `employee_code`. In this app these usually
                          // hold the same DZIND### value but match defensively).
                          const _hasLog = (set, e) => set.has(e.code) || set.has(e.id);
                          // Employees whose review for THIS stakeholder is already
                          // Submitted/Approved must be dropped from every reminder
                          // batch — nobody needs to be nagged for a review they've
                          // already filled. The chip row already paints them green
                          // with a ✓; this guarantees the bulk Send buttons also
                          // skip them so they never appear in the preview list.
                          const _isSubmitted = (e) => {
                            const rev = (allReviews || []).find(r =>
                              r.cycle_id === c.id && r.employee_id === e.id && r.stakeholder_id === sh.id
                            );
                            return rev && (rev.status === "Submitted" || rev.status === "Approved");
                          };
                          const reqPending = shEmps.filter(e => !_isSubmitted(e) && !_hasLog(reqSentSet, e));
                          const r1Pending = shEmps.filter(e => !_isSubmitted(e) && _hasLog(reqSentSet, e) && !_hasLog(r1SentSet, e));
                          const r2Pending = shEmps.filter(e => !_isSubmitted(e) && _hasLog(r1SentSet, e) && !_hasLog(r2SentSet, e));
                          const r3Pending = shEmps.filter(e => !_isSubmitted(e) && _hasLog(r2SentSet, e) && !_hasLog(r3SentSet, e));
                          const reqDoneCount = shEmps.filter(e => _hasLog(reqSentSet, e)).length;
                          const r1DoneCount = shEmps.filter(e => _hasLog(r1SentSet, e)).length;
                          const r2DoneCount = shEmps.filter(e => _hasLog(r2SentSet, e)).length;
                          const r3DoneCount = shEmps.filter(e => _hasLog(r3SentSet, e)).length;
                          // "Effectively done" — nobody's pending for this step
                          // anymore (either they got the mail or they already
                          // submitted their review, which excludes them from
                          // pending). Once the pending queue empties AND at
                          // least one send actually went out, flip the button
                          // to the coloured timestamp pill — even if some
                          // employees were submitted before they could be
                          // reminded.
                          const _done = (pending, doneCount) => pending.length === 0 && doneCount > 0;
                          const reqAllDone = _done(reqPending, reqDoneCount);
                          const r1AllDone = _done(r1Pending, r1DoneCount);
                          const r2AllDone = _done(r2Pending, r2DoneCount);
                          const r3AllDone = _done(r3Pending, r3DoneCount);
                          // Latest timestamp for the "all done" badge: most recent
                          // log entry of that type for any employee in shEmps.
                          const _latest = (kind) => {
                            const matching = _empLogs.filter(l => (kind === "request" ? _isReq(l) : _isRem(l, kind))
                              && shEmps.some(e => e.code === l.empId || e.id === l.empId));
                            if (!matching.length) return null;
                            return matching.reduce((acc, l) => (!acc || new Date(l.at) > new Date(acc)) ? l.at : acc, null);
                          };
                          const reqAllTime = _latest("request");
                          const r1AllTime = _latest(1);
                          const r2AllTime = _latest(2);
                          const r3AllTime = _latest(3);
                          // Legacy aliases kept for the email-log section below.
                          const reqSent = reqAllDone;
                          const r1Sent = r1AllDone;
                          const r2Sent = r2AllDone;
                          const r3Sent = r3AllDone;
                          // Each reminder slot exists iff a date was configured (c.reminders is
                          // the canonical N-length array set up by normalizeCycle).
                          const remindersArr = c.reminders || [];
                          const hasR1 = !!remindersArr[0];
                          const hasR2 = !!remindersArr[1];
                          const hasR3 = !!remindersArr[2];
                          // Deadline shown in each email = the NEXT touchpoint:
                          //   Request → R1   R1 → R2   R2 → R3   R3 → end_date + 90d
                          // If a "next" reminder slot is empty, fall back to end_date + 90
                          // (the final hard limit). Keeps preview in sync with the dispatcher.
                          const endPlus90 = addDaysIso(c.endDate, 90) || c.endDate;
                          const previewDl_request = c.r1 || endPlus90;
                          const previewDl_r1 = c.r2 || endPlus90;
                          const previewDl_r2 = c.r3 || endPlus90;
                          const previewDl_r3 = endPlus90;
                          // Hide reminder buttons if all employees for this stakeholder have submitted
                          const allSubmitted = shEmps.every(emp => {
                            const rev = (allReviews || []).find(r =>
                              r.cycle_id === c.id &&
                              r.employee_id === emp.id &&
                              r.stakeholder_id === sh.id
                            );
                            return rev && (rev.status === "Submitted" || rev.status === "Approved");
                          });
                          const logKey = shKey + "_log";
                          const logOpen = !!shLogOpen[logKey];

                          return (
                            <div key={sh.id} style={{
                              padding: "12px 20px 14px 36px",
                              borderTop: "1px dashed #E2E8F0", background: shIdx % 2 === 0 ? "#FAFBFF" : "#F8FAFC"
                            }}>

                              {/* Stakeholder info + action row */}
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                                <Avatar name={sh.name} size={30} />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{sh.name}</div>
                                  <div style={{ fontSize: 11, color: "#64748B" }}>{sh.designation} · {sh.email}</div>
                                </div>
                                {/* Primary badge: uses explicit primaryStakeholderId */}
                                {sh.level === "client" && (() => {
                                  const pid = cl.primaryStakeholderId
                                    ? cl.primaryStakeholderId
                                    : (cl.stakeholders.filter(s => s.active && s.level === "client")[0] || {}).id;
                                  return pid === sh.id;
                                })() && (
                                    <span style={{
                                      fontSize: 10, fontWeight: 700, background: "#FFF5F0", color: "#E8520A",
                                      border: "1px solid #FDBA74", padding: "2px 8px", borderRadius: 100
                                    }}>Primary</span>
                                  )}
                                <span style={{
                                  fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5,
                                  background: sh.level === "client" ? "#EFF6FF" : "#F5F3FF",
                                  color: sh.level === "client" ? "#3B82F6" : "#8B5CF6",
                                  padding: "3px 9px", borderRadius: 100
                                }}>
                                  {sh.level === "client" ? "All Employees" : "Dept Only"}
                                </span>
                                <span style={{ fontSize: 11, color: "#64748B" }}>{shEmps.length} emp{shEmps.length !== 1 ? "s" : ""}</span>

                                {/* Progress chips inline with the stakeholder header so the
                                    admin can see "X out of N done" at a glance for each step
                                    without expanding anything. */}
                                <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                                  {[
                                    { count: reqDoneCount, label: "Req", color: "#10B981", bg: "#F0FDF4", bd: "#BBF7D0" },
                                    ...(hasR1 ? [{ count: r1DoneCount, label: "R1", color: "#F59E0B", bg: "#FFFBEB", bd: "#FDE68A" }] : []),
                                    ...(hasR2 ? [{ count: r2DoneCount, label: "R2", color: "#8B5CF6", bg: "#F5F3FF", bd: "#DDD6FE" }] : []),
                                    ...(hasR3 ? [{ count: r3DoneCount, label: "R3", color: "#EF4444", bg: "#FEF2F2", bd: "#FECACA" }] : []),
                                  ].map(chip => (
                                    <span key={chip.label}
                                      style={{
                                        display: "inline-flex", alignItems: "center", gap: 5,
                                        fontSize: 10, fontWeight: 700, color: chip.color,
                                        background: chip.bg, border: `1px solid ${chip.bd}`,
                                        borderRadius: 100, padding: "2px 8px", whiteSpace: "nowrap",
                                      }}>
                                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: chip.color }} />
                                      {chip.label} {chip.count}/{shEmps.length}
                                    </span>
                                  ))}
                                </div>

                                {/* ── Stakeholder-level action buttons ─────────────────
                                    Each button targets ONLY the pending employees for that
                                    step. Employees who already got the mail via Review
                                    Management (per-employee) are excluded from the batch,
                                    so an admin clicking "Send Reminder 1" here can never
                                    re-send to someone who's already received R1. The
                                    Preview shows the exact recipient list before sending. */}
                                {!c.closed && currentRole !== "Sub-Admin" && (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end", marginLeft: "auto" }}>

                                    {[
                                      { key: "req", label: "Request Review", doneLabel: "Requested", type: "Review Request", deadline: previewDl_request, pending: reqPending, doneCount: reqDoneCount, lastSentAt: reqAllTime, btnClass: "btn-primary", sentColor: "#10B981", sentBg: "#F0FDF4", sentBd: "#BBF7D0", show: !allSubmitted },
                                      ...(hasR1 ? [{ key: "r1", label: "Send Reminder 1", doneLabel: "Reminder 1", type: "Reminder 1", deadline: previewDl_r1, pending: r1Pending, doneCount: r1DoneCount, lastSentAt: r1AllTime, btnClass: "btn-secondary", sentColor: "#F59E0B", sentBg: "#FFFBEB", sentBd: "#FDE68A", show: !allSubmitted }] : []),
                                      ...(hasR2 ? [{ key: "r2", label: "Send Reminder 2", doneLabel: "Reminder 2", type: "Reminder 2", deadline: previewDl_r2, pending: r2Pending, doneCount: r2DoneCount, lastSentAt: r2AllTime, btnClass: "btn-secondary", sentColor: "#8B5CF6", sentBg: "#F5F3FF", sentBd: "#DDD6FE", show: !allSubmitted }] : []),
                                      ...(hasR3 ? [{ key: "r3", label: "Send Reminder 3", doneLabel: "Reminder 3", type: "Reminder 3", deadline: previewDl_r3, pending: r3Pending, doneCount: r3DoneCount, lastSentAt: r3AllTime, btnClass: "btn-secondary", sentColor: "#EF4444", sentBg: "#FEF2F2", sentBd: "#FECACA", show: !allSubmitted }] : []),
                                    ].filter(s => s.show).map(step => {
                                      const pendingNames = step.pending.map(e => e.name).join(", ");
                                      // "Done" for display = nobody pending AND at least one
                                      // send happened. Includes the case where some employees
                                      // submitted before the step could be sent (they're
                                      // excluded from pending but don't count as recipients).
                                      const allDone = step.pending.length === 0 && step.doneCount > 0;
                                      // Cycle not activated yet → lock pill (same for every row)
                                      if (c.status !== "Active") {
                                        return (
                                          <span key={step.key} title="Activate the cycle first to send emails"
                                            style={{ fontSize: 11, color: "#94A3B8", background: "#F8FAFC", border: "1.5px dashed #CBD5E1", borderRadius: 7, padding: "5px 12px", fontWeight: 600, cursor: "not-allowed", whiteSpace: "nowrap" }}>
                                            🔒 {step.label}
                                          </span>
                                        );
                                      }
                                      // Invisible spacer that matches the 👁 preview button's
                                      // outer width — used on disabled rows so every row in the
                                      // stack ends at the same right edge AND aligns its main
                                      // button under the active row's button (not under the eye).
                                      const eyePlaceholder = (
                                        <span aria-hidden="true" style={{
                                          display: "inline-block",
                                          width: 30,    // matches preview button: padding (8+8) + icon (~13) + border (1.5×2)
                                          height: 22,
                                          visibility: "hidden",
                                        }} />
                                      );

                                      // All employees for this step have already received the mail
                                      // → render as a coloured badge pill with the inline
                                      //   timestamp, matching the original Cycle Scheduler look:
                                      //   "✓ Requested · 15 Jan 2026, 09:00 AM"
                                      if (allDone) {
                                        return (
                                          <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            {eyePlaceholder}
                                            <span style={{
                                              fontSize: 11, fontWeight: 700, color: step.sentColor,
                                              background: step.sentBg, border: `1px solid ${step.sentBd}`,
                                              borderRadius: 100, padding: "3px 10px", whiteSpace: "nowrap",
                                            }} title={`${step.doneLabel} sent to all ${shEmps.length} employees`}>
                                              ✓ {step.doneLabel}{step.lastSentAt ? ` · ${fmtDateTime(step.lastSentAt)}` : ""}
                                            </span>
                                          </div>
                                        );
                                      }
                                      // No pending employees yet (waiting on previous step's cascade)
                                      // → show disabled button so the admin sees the full lineup
                                      //   of reminders even before any prerequisite is met.
                                      if (step.pending.length === 0) {
                                        const reason = step.key === "req"
                                          ? "Nothing to request"
                                          : "Send the previous step first";
                                        return (
                                          <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            {eyePlaceholder}
                                            <button className={step.btnClass}
                                              disabled
                                              title={reason}
                                              style={{ fontSize: 11, padding: "5px 12px", whiteSpace: "nowrap", opacity: 0.4, cursor: "not-allowed" }}>
                                              {step.label}
                                            </button>
                                          </div>
                                        );
                                      }
                                      // Active state — pending recipients exist, button + preview enabled.
                                      // No timestamp shown here: the timestamp only appears in
                                      // the completed-pill state (matches the reference design).
                                      return (
                                        <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                          <button title={`Preview ${step.type} — recipients: ${pendingNames}`}
                                            onClick={() => openPreview(c.id, cl.id, sh.id, sh.name, sh.email, step.pending, step.type, c.q, step.deadline)}
                                            style={{ background: "none", border: "1.5px solid #CBD5E1", borderRadius: 7, padding: "3px 8px", cursor: "pointer", fontSize: 13, color: "#64748B" }}>
                                            &#128065;
                                          </button>
                                          <button className={step.btnClass}
                                            title={`Will send to ${step.pending.length} of ${shEmps.length} employees: ${pendingNames}`}
                                            style={{ fontSize: 11, padding: "5px 12px", whiteSpace: "nowrap" }}
                                            onClick={() => sendStakeholderEmail(c.id, cl.id, sh.id, sh.name, sh.email, step.pending, c.q, step.type)}>
                                            {step.label}
                                            {shEmps.length > 1 && step.pending.length < shEmps.length && (
                                              <span style={{ marginLeft: 4, opacity: 0.85, fontWeight: 600 }}>({step.pending.length})</span>
                                            )}
                                          </button>
                                        </div>
                                      );
                                    })}

                                    {allSubmitted && reqSent && (
                                      <span style={{
                                        fontSize: 11, color: "#10B981", fontWeight: 700, background: "#F0FDF4",
                                        border: "1px solid #BBF7D0", borderRadius: 100, padding: "3px 9px", whiteSpace: "nowrap"
                                      }}>
                                        ✓ All Submitted
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* ── Employee chips (compact roster) ───────────────────
                                  Each chip shows name · ID · allocation%. Per-employee
                                  send timestamps still live on the Review Management
                                  page — this card surfaces the roster + bulk actions. */}
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 4 }}>
                                {shEmps.map(emp => {
                                  const alloc = emp.allocations.find(a => a.clientId === cl.id);
                                  const shEntry = alloc && alloc.stakeholders && alloc.stakeholders.length > 0
                                    ? alloc.stakeholders.find(s => s.stakeholderId === sh.id)
                                    : null;
                                  const pct = shEntry ? shEntry.pct : (alloc ? alloc.pct : null);
                                  const isCustom = shEntry !== null && shEntry !== undefined;

                                  const empRev = (allReviews || []).find(r =>
                                    r.cycle_id === c.id && r.employee_id === emp.id && r.stakeholder_id === sh.id
                                  );
                                  const rawStatus = empRev && empRev.status ? empRev.status : "Not Started";
                                  const empStatus = rawStatus === "Approved" ? "Submitted" : rawStatus;
                                  const empSubmitted = empStatus === "Submitted";

                                  const STATUS_PALETTE = {
                                    "Not Started": { bg: "#F8FAFC", color: "#94A3B8", border: "#E2E8F0" },
                                    "Initiated": { bg: "#EEF2FF", color: "#4338CA", border: "#C7D2FE" },
                                    "In Progress": { bg: "#FEF3C7", color: "#92400E", border: "#FDE68A" },
                                    "Submitted": { bg: "#D1FAE5", color: "#065F46", border: "#A7F3D0" },
                                  };
                                  const sp = STATUS_PALETTE[empStatus] || STATUS_PALETTE["Not Started"];

                                  return (
                                    <div key={emp.id} title={`${emp.name} · ${emp.code} · ${empStatus}`}
                                      style={{
                                        display: "flex", alignItems: "flex-start", gap: 7,
                                        background: empSubmitted ? "#F0FDF4" : "#fff",
                                        border: "1.5px solid " + (empSubmitted ? "#BBF7D0" : "#E2E8F0"),
                                        borderRadius: 8, padding: "5px 10px",
                                        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
                                      }}>
                                      <Avatar name={emp.name} size={22} />
                                      <div style={{ minWidth: 0, flex: 1, display: "flex", gap: 6, alignItems: "flex-start" }}>
                                        {/* Left: name + role */}
                                        <div style={{ minWidth: 0 }}>
                                          <div style={{ fontSize: 12, fontWeight: 600, color: "#1E293B", lineHeight: 1.2 }}>
                                            {emp.name}
                                          </div>
                                          {emp.role && (
                                            <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>{emp.role}</div>
                                          )}
                                        </div>
                                        {/* Right: code + status — both left-aligned at the same x position */}
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3, flexShrink: 0 }}>
                                          <span style={{
                                            fontSize: 9, fontFamily: "'JetBrains Mono',monospace",
                                            color: "#64748B", background: "#F8FAFC",
                                            border: "1px solid #E2E8F0", borderRadius: 3, padding: "0 4px", lineHeight: 1.6,
                                          }}>{emp.code}</span>
                                          <span style={{
                                            display: "inline-flex", alignItems: "center", gap: 3,
                                            fontSize: 8, fontWeight: 700,
                                            color: sp.color, background: sp.bg,
                                            border: `1px solid ${sp.border}`,
                                            borderRadius: 100, padding: "1px 6px",
                                            textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap",
                                          }}>
                                            {empSubmitted
                                              ? <span style={{ fontSize: 9, lineHeight: 1 }}>✓</span>
                                              : <span style={{ width: 4, height: 4, borderRadius: "50%", background: sp.color, display: "inline-block", opacity: 0.7 }} />
                                            }
                                            {empStatus}
                                          </span>
                                        </div>
                                      </div>
                                      {pct !== null && (
                                        <span style={{
                                          fontSize: 10, fontWeight: 800,
                                          color: isCustom ? "#6D28D9" : clColor,
                                          background: isCustom ? "#F5F3FF" : clColor + "18",
                                          border: "1px solid " + (isCustom ? "#DDD6FE" : clColor + "44"),
                                          padding: "2px 7px", borderRadius: 100, marginLeft: 2
                                        }}>
                                          {pct}%{isCustom ? " ✶" : ""}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Per-stakeholder email log (collapsible) */}
                              {shState.logs && shState.logs.length > 0 && (
                                <div style={{ marginTop: 10, paddingLeft: 4 }}>
                                  <button onClick={() => togShLog(logKey)}
                                    style={{
                                      background: "none", border: "none", cursor: "pointer", fontSize: 11,
                                      color: "#64748B", fontWeight: 600, display: "flex", alignItems: "center", gap: 4, padding: 0
                                    }}>
                                    &#128203; Email Log ({shState.logs.length}) {logOpen ? "▲" : "▼"}
                                  </button>
                                  {logOpen && (
                                    <div className="slide-down" style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                                      {shState.logs.map((log, li) => (
                                        <div key={li} style={{
                                          display: "flex", alignItems: "center", gap: 10,
                                          background: "#fff", border: "1px solid #E2E8F0", borderRadius: 7, padding: "7px 12px"
                                        }}>
                                          <div style={{
                                            width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background:
                                              log.type === "Review Request" ? "#10B981" :
                                                log.type === "Reminder 1" ? "#F59E0B" :
                                                  log.type === "Reminder 2" ? "#8B5CF6" : "#EF4444"
                                          }} />
                                          <span style={{ fontSize: 12, fontWeight: 600, color: "#1E293B", minWidth: 100 }}>{log.empName}</span>
                                          <span style={{
                                            fontSize: 11, background:
                                              log.type === "Review Request" ? "#F0FDF4" :
                                                log.type === "Reminder 1" ? "#FFFBEB" :
                                                  log.type === "Reminder 2" ? "#F5F3FF" : "#FEF2F2",
                                            color:
                                              log.type === "Review Request" ? "#059669" :
                                                log.type === "Reminder 1" ? "#D97706" :
                                                  log.type === "Reminder 2" ? "#7C3AED" : "#DC2626",
                                            border: "1px solid", borderColor:
                                              log.type === "Review Request" ? "#BBF7D0" :
                                                log.type === "Reminder 1" ? "#FDE68A" :
                                                  log.type === "Reminder 2" ? "#DDD6FE" : "#FECACA",
                                            borderRadius: 100, padding: "2px 8px", fontWeight: 600
                                          }}>{log.type}</span>
                                          <span style={{ fontSize: 11, color: "#94A3B8", marginLeft: "auto" }}>{fmtDateTime(log.at)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}

                  {/* ── Unallocated employees warning ── */}
                  {employees.filter(e => e.allocations.length === 0).length > 0 && (
                    <div style={{
                      padding: "14px 20px", background: "#FEF2F2", borderTop: "1.5px solid #FECACA",
                      display: "flex", alignItems: "flex-start", gap: 12
                    }}>
                      <div style={{ fontSize: 20, flexShrink: 0 }}>⚠️</div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#991B1B", marginBottom: 8 }}>
                          {employees.filter(e => e.allocations.length === 0).length} unallocated employee{employees.filter(e => e.allocations.length === 0).length !== 1 ? "s" : ""} — not scoped to any client review
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {employees.filter(e => e.allocations.length === 0).map(emp => (
                            <div key={emp.id} style={{
                              display: "flex", alignItems: "center", gap: 6,
                              background: "#fff", border: "1.5px solid #FECACA", borderRadius: 8, padding: "5px 10px"
                            }}>
                              <Avatar name={emp.name} size={20} />
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#991B1B" }}>{emp.name}</span>
                              <span style={{ fontSize: 10, color: "#EF4444", fontWeight: 600 }}>0% allocated</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isHist && (
                <div className="slide-down" style={{ borderTop: "1px solid #F1F5F9", padding: "16px 20px", background: "#FAFBFF" }}>
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>
                    Email Activity Timeline — {c.q}
                  </div>
                  {c.emailHistory.length === 0 ? (
                    <div style={{ fontSize: 13, color: "#94A3B8", fontStyle: "italic" }}>No emails triggered yet for this cycle.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {c.emailHistory.map((h, i) => {
                        const dot = h.type === "Review Request" ? "#10B981" : h.type === "Reminder 1" ? "#F59E0B" :
                          h.type === "Reminder 2" ? "#8B5CF6" : h.type === "Reminder 3" ? "#EF4444" :
                            h.type === "Submission Confirm" ? "#3B82F6" : "#64748B";
                        return (
                          <div key={i} style={{
                            display: "flex", alignItems: "center", gap: 12, background: "#fff",
                            border: "1px solid #E2E8F0", borderRadius: 9, padding: "10px 14px"
                          }}>
                            <div style={{ width: 9, height: 9, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{h.type}</div>
                              <div style={{ fontSize: 11, color: "#64748B", marginTop: 1 }}>{h.notes}</div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <span style={{
                                fontSize: 11, fontWeight: 600, background: dot + "18",
                                color: dot, border: "1px solid " + dot + "33", borderRadius: 100, padding: "2px 9px", display: "inline-block", marginBottom: 3
                              }}>
                                {h.recipients} Recipient{h.recipients !== 1 ? "s" : ""}
                              </span>
                              {/* Backend now sends ISO timestamps; optimistic-UI
                                  entries still send pre-formatted strings, so
                                  feed both through fmtDateTime which is a no-op
                                  for already-formatted strings only when they
                                  parse as a Date — fallback to the raw string. */}
                              <div style={{ fontSize: 11, color: "#94A3B8" }}>{h.at && !isNaN(new Date(h.at)) ? fmtDateTime(h.at) : h.at}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showNew && <NewCycleModal onAdd={addCycle} onClose={() => setShowNew(false)} existing={cycles} />}

      {/* ── Email Preview Modal ── */}
      {previewModal && (
        <div className="modal-overlay" style={{ left: 236 }}>
          <div className="modal" style={{ maxWidth: 600, width: "min(600px, calc(100% - 32px))" }}>
            {/* Header */}
            <div style={{
              padding: "20px 28px", borderBottom: "1px solid #F1F5F9",
              background: "linear-gradient(135deg,#0D1B2A 0%,#1E3A5F 100%)", borderRadius: "14px 14px 0 0",
              display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#fff" }}>
                  &#128065; Preview Email
                </div>
                <div style={{ fontSize: 12, color: "#8CA4BE", marginTop: 3 }}>{previewModal.type} → {previewModal.shName}</div>
              </div>
              <button onClick={() => setPreviewModal(null)} style={{ background: "none", border: "none", color: "#8CA4BE", fontSize: 22, cursor: "pointer" }}>&#215;</button>
            </div>

            {/* Email preview body */}
            <div style={{ padding: "24px 28px" }}>
              {/* To / Subject */}
              <div style={{ background: "#F8FAFC", borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: "1px solid #E2E8F0" }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, width: 52 }}>TO:</span>
                  <span style={{ fontSize: 12, color: "#0D1B2A", fontWeight: 600 }}>{previewModal.shName} &lt;{previewModal.shEmail}&gt;</span>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, width: 52 }}>CC:</span>
                  <span style={{ fontSize: 12, color: "#64748B" }}>connect@dolluzcorp.com (Super Admin) + CC list</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, width: 52 }}>SUBJECT:</span>
                  <span style={{ fontSize: 12, color: "#0D1B2A", fontWeight: 600 }}>
                    {renderPreviewSubject() || previewModal.quarter}
                  </span>
                </div>
              </div>

              {/* Email body preview — driven by DB templates (same as Email Module) */}
              <div style={{
                background: "#FAFBFF", borderRadius: 10, padding: "16px 18px", border: "1px solid #E2E8F0",
                fontFamily: "'DM Sans',sans-serif", fontSize: 13, lineHeight: 1.7, color: "#1E293B", whiteSpace: "pre-wrap", maxHeight: 280, overflowY: "auto"
              }}>
                {renderPreviewBody()}
              </div>

              {/* CC note */}
              <div style={{ marginTop: 12, background: "#FFF5F0", border: "1px solid #FED7AA", borderRadius: 8, padding: "8px 14px", fontSize: 11, color: "#92400E", display: "flex", alignItems: "center", gap: 6 }}>
                &#128274; All outgoing emails automatically include CC recipients configured in Email → CC Management.
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
                <button className="btn-ghost" onClick={() => setPreviewModal(null)}>Cancel</button>
                <button className="btn-primary" onClick={() =>
                  sendStakeholderEmail(previewModal.cycId, previewModal.clId, previewModal.shId, previewModal.shName, previewModal.shEmail, previewModal.empList, previewModal.quarter, previewModal.type)}>
                  &#128231; Send {previewModal.type}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast} type={toastType} />}
    </div>
  );
};

export { NewCycleModal };
export default Scheduler;